import React, { FC } from 'react';
import { Button, Icon, StsIconName, Typography } from '@packages/ui/shared';
import { Collapse } from 'react-bootstrap';
import { createUseStyles } from 'react-jss';
import { Document } from '@web/vin-sessions/assets/Icons';
import { Link } from 'react-router-dom';
import { StyleSheet } from 'react-native';
import colors from '@packages/core/styles/colors';
import fonts from '@web/jss/fonts';

const useNavigationItemStyles = createUseStyles({
    navigationItem: ({ isActive, mainNav, isAdminHub }: NavigationItemProps) => ({
        borderRight: isActive && isAdminHub ? 'none' : `1px solid ${colors.grayThree}`,
        backgroundColor: isActive ? colors.white : 'transparent',
        borderTop: `1px solid ${mainNav && isActive ? colors.grayThree : 'transparent'}`,
        borderBottom: `1px solid ${isActive ? colors.grayThree : 'transparent'}`,
        '&:hover': {
            backgroundColor: isActive ? colors.white : colors.grayThree,
        },
    }),
    header: (props: NavigationItemProps) => ({
        display: 'flex',
        position: 'relative',
        alignItems: 'center',
        justifyContent: 'space-between',
        '& > a, & > span': {
            cursor: 'pointer',
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            padding: props.mainNav ? 14 : 20,
            '&:hover': {
                textDecoration: 'none',
            },
        },
        '& span': {
            color: props.isActive ? colors.blueOne : colors.graySix,
        },
        '&:hover': {
            '& img': {
                backgroundColor: colors.blueOne,
            },
            '& span': {
                color: colors.blueOne,
            },
        },
        backgroundColor: props.isMainNavActive ? colors.blueOne : 'transparent',
    }),
    icon: {
        display: 'flex',
        marginRight: 14,
    },
    notificationCount: {
        height: 16,
        minWidth: 16,
        ...fonts.xxs,
        marginLeft: 4,
        padding: '0 4px',
        borderRadius: 8,
        textAlign: 'center',
        color: colors.white,
        lineHeight: '1.5rem',
        ...fonts.robotoRegular,
        backgroundColor: colors.redOne,
    },
    stisNotificationCount: {
        alignItems: 'center',
        backgroundColor: '#dee6ef',
        borderRadius: 100,
        display: 'flex',
        height: 20,
        justifyContent: 'center',
        // Grows with the number: a fixed width left a four-digit count hanging outside the pill.
        minWidth: 31,
        padding: '0 8px',
        boxSizing: 'border-box',
        flexShrink: 0,
        marginRight: 8,
    },
});

const styles = StyleSheet.create({
    count: {
        color: colors.blueOne,
        fontWeight: 'bold',
        fontSize: 14,
    },
});

interface NavigationItemProps {
    to?: string;
    title: string;
    iconName?: StsIconName | 'techline-logo';
    open?: boolean;
    notificationCount?: number;
    isActive?: boolean;
    mainNav?: boolean;
    isMainNavActive?: boolean;
    onCollapseToggle?(open: boolean): void;
    onClick?(e?: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void;
    onHover?(title: string, ref?: React.ReactNode): void;
    onHoverLeave?(title: string, ref?: React.ReactNode): void;
    children?: React.ReactNode;
    stisIconFlag?: boolean;
    /** An icon the shared set does not carry, e.g. a publication type icon. */
    customIcon?: React.ReactNode;
    recordsCount?: number;
    isAdminHub?: boolean;
    displayFlag?: boolean;
}

const NavigationItem: FC<NavigationItemProps> = ({
    children,
    onHover,
    onHoverLeave,
    to,
    title,
    iconName,
    open = false,
    notificationCount,
    isActive,
    isAdminHub,
    mainNav = true,
    isMainNavActive = false,
    onCollapseToggle,
    onClick,
    stisIconFlag,
    customIcon,
    recordsCount,
    displayFlag,
}) => {
    const hoverRefEl = React.useRef(null);

    const classes: any = useNavigationItemStyles({
        isActive,
        mainNav,
        hasChildren: !children,
        isMainNavActive,
        isAdminHub,
    });

    function handleChevronButtonClick() {
        if (onCollapseToggle) {
            onCollapseToggle(!open);
        }
    }

    const handleHover = () => {
        if (onHover) {
            onHover(title, hoverRefEl?.current);
        }
    };

    const handleHoverLeave = () => {
        if (onHoverLeave) {
            onHoverLeave(title, hoverRefEl?.current);
        }
    };

    const WrapperComponent = !!to ? Link : 'span';

    return (
        displayFlag && (
            <div className={classes.navigationItem}>
                <div className={classes.header}>
                    <WrapperComponent
                        to={to || ''}
                        onClick={(e: any) => {
                            onClick && onClick(e);
                        }}
                        onMouseEnter={handleHover}
                        onMouseLeave={handleHoverLeave}
                        ref={hoverRefEl}
                    >
                        {iconName && iconName !== 'techline-logo' && (
                            <div className={classes.icon}>
                                <Icon
                                    name={iconName as StsIconName}
                                    size={20}
                                    color={isMainNavActive ? 'white' : isActive ? 'blueOne' : 'textDarkSecondary'}
                                />
                            </div>
                        )}

                        {stisIconFlag && (
                            <div className={classes.icon}>
                                <Document color={isActive ? colors.blueOne : '#4C525D'} />
                            </div>
                        )}

                        {customIcon && <div className={classes.icon}>{customIcon}</div>}

                        {iconName === 'techline-logo' && (
                            <div className={classes.icon}>
                                <svg
                                    width="24"
                                    height="26"
                                    viewBox="0 0 59.14 56.81"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g id="Left Icon">
                                        <path
                                            id="Vector"
                                            d="M29.11,47.61c-1.3,0-2.47-.62-3.08-1.61,0,0-.06-.04-.09-.05-4.66-1.1-8.31-3.8-10.85-8.01-.05-.09-.08-.11-.08-.12-.11.04-.24.04-.37.04-.11,0-.46-.01-.46-.01-1.92-.05-3.42-1.52-3.5-3.42,0-.1-.01-.13-.01-.14-.7-.24-1.12-.9-1.12-1.75-.01-1.27-.01-2.53,0-3.8,0-.55.21-1.33,1.14-1.85.04-1.46.82-2.58,2.26-3.25h0s.06-.13.12-.32c1.42-4.67,4.2-8.22,8.24-10.56,2.44-1.41,5.23-2.15,8.06-2.15,4.64,0,9.09,1.98,12.22,5.42,1.99,2.19,3.38,4.69,4.15,7.45.05.18.08.19.19.24,1.34.57,2.09,1.65,2.16,3.1,0,.05,0,.08,0,.1.72.31,1.12.95,1.13,1.79.01,1.27.01,2.53,0,3.8,0,.8-.39,1.44-1.11,1.86-.16,2.06-1.64,3.46-3.69,3.47h-.67c-1.28-.02-2.05-.79-2.06-2.07,0-.61,0-1.23,0-1.84v-4.7c0-1.23,0-2.47,0-3.7,0-.77.29-1.37.86-1.8-1.75-5.46-6.36-9.36-11.79-9.95-.46-.05-.92-.07-1.39-.07-5.18,0-10.05,3.16-12.39,8.05-.31.65-.56,1.35-.78,1.98.56.41.84.97.84,1.67,0,3.57-.01,7.14,0,10.71,0,.11.03.23.07.31,2.08,3.63,5.15,5.98,9.14,6.98.09.02.13.03.15.03h0s.03-.01.09-.07c.62-.64,1.54-1,2.53-1s1.85.34,2.47.93c.94.88,1,2.27.13,3.23-.61.67-1.54,1.06-2.55,1.06ZM33.78,53.09h2.13c3.59-1.11,3.54,2.94,6,3.71,1.57-.94,3.28-1.96,5.02-3-.67-1.82-1.22-3.33-1.83-5,.74-.74,1.53-1.4,2.16-2.19.89-1.13,1.83-1.33,3.11-.66.96.5,2.03.81,3.24,1.27,1.11-1.92,2.11-3.66,3.15-5.47-1.55-1.33-2.89-2.48-4.21-3.61.45-1.55.85-2.92,1.23-4.26,1.84-.29,3.52-.56,5.36-.85-.02-2.18-.04-4.2-.06-6.35-1.84-.33-3.57-.63-5.32-.94-.4-1.6-.77-3.08-1.13-4.56,1.42-1.21,2.75-2.35,4.15-3.55-1.17-1.99-2.19-3.71-3.19-5.41-1.82.66-3.37,1.78-4.67,1.53-1.36-.25-2.48-1.82-3.71-2.82.7-1.93,1.31-3.6,1.98-5.46-1.91-1.08-3.71-2.11-5.48-3.12-1.35,1.6-2.44,2.9-3.55,4.22-1.59-.42-3.07-.8-4.63-1.21-.31-1.8-.61-3.54-.91-5.3-2.2-.02-4.22-.04-6.36-.06-.32,1.87-.63,3.61-.93,5.37-1.61.42-3.09.8-4.58,1.19-1.21-1.42-2.35-2.75-3.6-4.22-1.92,1.14-3.65,2.16-5.41,3.19.71,1.92,1.3,3.51,1.89,5.1-1.11,1.11-2.13,2.13-3.23,3.23-1.66-.6-3.32-1.2-5.17-1.86-1.08,1.89-2.07,3.63-3.11,5.46,1.58,1.33,2.93,2.47,4.23,3.56-.46,1.67-.87,3.15-1.31,4.74-1.76.3-3.5.61-4.9.85-.05,2.29-.09,4.31-.14,6.51,1.12.16,2.29.42,3.46.47,1.36.06,1.97.71,2.17,2,.15.99.47,1.95.69,2.83-1.43,1.2-2.67,2.25-4.21,3.54,1.1,1.9,2.11,3.64,3.16,5.44,1.02-.37,1.82-.66,2.61-.96.87-.32,1.74-.64,2.52-.93q3.51,2.98,2.01,7.01c-.14.39-.2.8-.34,1.39,1.82,1.03,3.57,2.02,5.11,2.89,2.32-1.08,2.36-4.63,5.76-3.74h2.09s.11.01.11.01c0,0-.02-6.3-.02-6.23l.23.06c.84,1.1,2.24,1.77,3.75,1.77,1.31,0,2.53-.52,3.35-1.42,1.27-1.41,1.19-3.45-.2-4.74-.83-.77-2-1.22-3.21-1.22-1.08,0-2.11.35-2.9.97l-.98-.06c0,.06,0-.98,0-.98.02-1.28-.32-2.03-1.48-2.75-6.76-4.2-6.77-14.03-.09-18.2.33-.2.72-.31,1.27-.55,0,3.05-.02,5.85.02,8.64,0,.5.13,1.16.46,1.46,2.12,1.9,4.15,2.01,6.78.77,1.34-.62,1.86-1.38,1.78-2.88-.13-2.6-.03-5.22-.03-8.2.84.5,1.36.76,1.82,1.1,6.27,4.52,5.93,13.78-.63,17.97-.56.36-1.3,1.04-1.31,1.58-.11,4.88-.06,9.82-.06,12.9h.09Z"
                                            fill={
                                                isMainNavActive
                                                    ? colors.white
                                                    : isActive
                                                      ? colors.blueOne
                                                      : colors.textDarkSecondary
                                            }
                                        />
                                    </g>
                                </svg>
                            </div>
                        )}

                        <Typography
                            variant="label"
                            color={isMainNavActive ? 'white' : isActive ? 'blueOne' : 'textDarkSecondary'}
                            style={{
                                marginTop: 2,
                                maxWidth:
                                    children && recordsCount !== undefined && recordsCount !== null && recordsCount >= 0
                                        ? 100
                                        : 'auto',
                            }}
                            ellipsizeMode="tail"
                            numberOfLines={1}
                        >
                            {title}
                        </Typography>

                        {!!notificationCount && <div className={classes.notificationCount}>{notificationCount}</div>}
                    </WrapperComponent>

                    {children && (
                        <Button variant="link" onPress={handleChevronButtonClick} style={{ width: 44 }}>
                            <Icon
                                name={open ? 'arrow-small-up' : 'arrow-small-down'}
                                size={16}
                                color={isMainNavActive ? 'white' : isActive ? 'blueOne' : 'textDarkSecondary'}
                            />
                        </Button>
                    )}
                    {recordsCount !== undefined && recordsCount !== null && recordsCount >= 0 ? (
                        <div className={classes.stisNotificationCount}>
                            <Typography style={styles.count}>{recordsCount}</Typography>
                        </div>
                    ) : null}
                </div>

                {children && (
                    <Collapse in={open}>
                        <div>{children}</div>
                    </Collapse>
                )}
            </div>
        )
    );
};

export default NavigationItem;
